import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import useSessionTrackingStore from '@/stores/sessionTrackingStore';
import {
  concludeActionSession,
  isActionSessionRecord,
  OPEN_ACTION_SESSION_STATUSES,
  reconcileReportedSession,
  startActionSession,
  subscribeStaffActionSessions,
} from '../services/actionSessionService';

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return Number(value) || 0;
}

function makeRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useDurableActionSession() {
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const serverSessionId = useSessionTrackingStore(state => state.serverSessionId);
  const serverSessionStatus = useSessionTrackingStore(state => state.serverSessionStatus);
  const serverSessionRecovery = useSessionTrackingStore(state => state.serverSessionRecovery);
  const localActions = useSessionTrackingStore(state => state.sessionActions);
  const inFlightStartRef = useRef(null);
  const markSessionConcluded = useCallback(sessionId => {
    useSessionTrackingStore.getState().markServerSessionConcluded({
      schoolId, userId: user?.uid, sessionId,
    });
  }, [schoolId, user?.uid]);
  const isSessionConcluded = useCallback(sessionId =>
    useSessionTrackingStore.getState().hasConcludedServerSession({
      schoolId, userId: user?.uid, sessionId,
    }), [schoolId, user?.uid]);

  useEffect(() => {
    if (!schoolId || !user?.uid || serverSessionId || !localActions.length) return;
    let cancelled = false;
    let checking = false;
    const reconcile = async () => {
      if (checking || cancelled) return;
      const state = useSessionTrackingStore.getState();
      if (state.serverSessionId || state.sessionActions !== localActions) return;
      checking = true;
      try {
        const result = await reconcileReportedSession({
          schoolId,
          consequenceType: state.primaryConsequenceType,
          conductedBy: {uid: user.uid},
          actions: Object.fromEntries(localActions),
        });
        const current = useSessionTrackingStore.getState();
        if (cancelled || current.serverSessionId || current.sessionActions !== localActions) return;
        const reported = new Set(result.reportedRecordIds || []);
        if (!reported.size) return;
        const remaining = localActions.filter(([id]) => !reported.has(id));
        if (!remaining.length) {
          current.clearSession();
          useSessionTrackingStore.setState({concludedSession: null});
        } else {
          useSessionTrackingStore.setState({
            sessionActions: remaining,
            sessionActionedIds: remaining.map(([id]) => id),
            sessionRosterRecordIds: current.sessionRosterRecordIds.filter(id => !reported.has(id)),
          });
        }
      } catch (error) {
        console.error('[ActionSession] Saved report reconciliation failed:', error);
      } finally {
        checking = false;
      }
    };
    reconcile();
    const interval = setInterval(reconcile, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') reconcile(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [schoolId, user?.uid, serverSessionId, localActions]);

  useEffect(() => {
    return subscribeStaffActionSessions({
      schoolId,
      userId: user?.uid,
      onSessions: sessions => {
        let state = useSessionTrackingStore.getState();
        const suppliedCurrent = sessions.find(
          session => session.id === state.serverSessionId
        );
        if (suppliedCurrent && !isActionSessionRecord(suppliedCurrent)) {
          state.clearSession();
          state = useSessionTrackingStore.getState();
        }
        const actionSessions = sessions.filter(isActionSessionRecord);
        actionSessions.forEach(session => {
          if (session.status === 'concluded') {
            markSessionConcluded(session.id);
          }
        });
        const current = actionSessions.find(
          session => session.id === state.serverSessionId
        );
        if (current && (current.status === 'concluded' || isSessionConcluded(current.id))) {
          state.clearSession();
          useSessionTrackingStore.setState({concludedSession: null});
          state = useSessionTrackingStore.getState();
        }
        if (current && OPEN_ACTION_SESSION_STATUSES.has(current.status) &&
            !isSessionConcluded(current.id)) {
          state.syncServerSession(current.id, current);
          return;
        }
        if (state.serverSessionId) return;
        const resumable = actionSessions
          .filter(session => OPEN_ACTION_SESSION_STATUSES.has(session.status) &&
            !isSessionConcluded(session.id))
          .sort((left, right) =>
            timestampMillis(right.lastActivityAt) - timestampMillis(left.lastActivityAt)
          )[0];
        if (resumable) state.syncServerSession(resumable.id, resumable);
      },
      onError: error => {
        console.error('[ActionSession] Subscription failed:', error);
      },
    });
  }, [schoolId, user?.uid, markSessionConcluded, isSessionConcluded]);

  const ensureSession = useCallback(async ({
    consequenceType,
    consequenceTypeLabel,
    rosterRecordIds,
    platform = 'web',
  }) => {
    const state = useSessionTrackingStore.getState();
    if (state.serverSessionId && OPEN_ACTION_SESSION_STATUSES.has(state.serverSessionStatus)) {
      if (state.primaryConsequenceType !== consequenceType) {
        throw new Error(
          `Conclude the open ${state.primaryConsequenceTypeLabel || 'action'} session first.`
        );
      }
      return state.serverSessionId;
    }

    // A browser-local session created before this rollout retains the legacy
    // report path. Do not silently give its already-written actions a new
    // server boundary.
    if (!state.serverSessionId && state.sessionActions.length > 0) return null;
    if (inFlightStartRef.current) return inFlightStartRef.current;

    const requestId = state.serverSessionRequestId || makeRequestId();
    state.setServerSessionRequest(requestId);
    const startPromise = startActionSession({
      schoolId,
      requestId,
      rosterRecordIds,
      consequenceType,
      consequenceTypeLabel,
      platform,
    }).then(result => {
      const current = useSessionTrackingStore.getState();
      if (result.session?.status === 'concluded') markSessionConcluded(result.sessionId);
      if (isSessionConcluded(result.sessionId) &&
          !current.serverSessionId && current.serverSessionRequestId === requestId) {
        current.setServerSessionRequest(null);
      }
      if (!isSessionConcluded(result.sessionId) &&
          (!current.serverSessionId || current.serverSessionId === result.sessionId)) {
        current.syncServerSession(result.sessionId, result.session);
      }
      return result.sessionId;
    }).finally(() => {
      inFlightStartRef.current = null;
    });
    inFlightStartRef.current = startPromise;
    return startPromise;
  }, [schoolId, isSessionConcluded, markSessionConcluded]);

  const concludeSession = useCallback(async notes => {
    const state = useSessionTrackingStore.getState();
    if (!state.serverSessionId) return null;
    const consequenceType = state.primaryConsequenceType;
    const consequenceTypeLabel = state.primaryConsequenceTypeLabel;
    const sessionId = state.serverSessionId;
    const result = await concludeActionSession({
      schoolId,
      sessionId,
      notes: notes || '',
    });
    markSessionConcluded(sessionId);
    if (useSessionTrackingStore.getState().serverSessionId === sessionId) {
      state.concludeSession({consequenceType, consequenceTypeLabel});
    }
    return result;
  }, [schoolId, markSessionConcluded]);

  const reconcileActionResult = useCallback((result, requestedSessionId) => {
    const state = useSessionTrackingStore.getState();
    const responseSessionId = result?.sessionId || requestedSessionId;
    if (responseSessionId && isSessionConcluded(responseSessionId)) {
      return 'closed_session';
    }
    if (responseSessionId && state.serverSessionId &&
        responseSessionId !== state.serverSessionId) return 'stale_session';
    if (result?.attendanceRecoveryRegisterId) {
      const suppliedSessionBecameUntrusted = requestedSessionId &&
        state.serverSessionId === requestedSessionId;
      const legacyLocalRegister = !state.serverSessionId &&
        state.sessionActions.length > 0;
      if (suppliedSessionBecameUntrusted || legacyLocalRegister) {
        state.clearSession();
      }
      return 'attendance_recovery';
    }
    if (result?.sessionLinkageRecovered && result.sessionId && result.session) {
      state.syncServerSession(result.sessionId, result.session);
      return 'session_link_recovered';
    }
    return 'session';
  }, [isSessionConcluded]);

  return {
    serverSessionId,
    serverSessionStatus,
    serverSessionRecovery,
    ensureSession,
    reconcileActionResult,
    concludeServerSession: concludeSession,
  };
}
